import { fmt, monthYearOf, tagLabel, tagStyle, withUnit } from './format';

// Visits shown before "Show all"
export const VISIT_LIMIT = 5;

const visitKey = (visit, index) => String(visit.n != null ? visit.n : index);

const summaryOf = (visit, t) => {
    const ops = visit.ops || [];
    const parts = visit.parts || [];
    if (visit.remark) return visit.remark;
    if (ops.length) {
        const more = Math.max(0, ops.length - 2) + (visit.extraOps || 0);
        return (
            ops
                .slice(0, 2)
                .map(o => o.text)
                .join(' · ') + (more > 0 ? ` · ${t('journey.more', { count: more })}` : '')
        );
    }
    if (parts.length) {
        const more = parts.length - 2;
        return (
            parts
                .slice(0, 2)
                .map(p => p.text || p.no)
                .join(' · ') + (more > 0 ? ` · ${t('journey.more', { count: more })}` : '')
        );
    }
    return t('journey.serviceVisit');
};

const buildVisit = (visit, index, open, t) => {
    const key = visitKey(visit, index);
    const ops = visit.ops || [];
    const parts = visit.parts || [];
    const refs = visit.refs || [];
    const hasDetail = ops.length > 0 || parts.length > 0 || refs.length > 0 || !!visit.complaint;
    const isOpen = (open[key] !== undefined ? open[key] : !!visit.open) && hasDetail;
    const showCodes = ops.some(o => !!o.code);

    return {
        key,
        raw: visit,
        ...monthYearOf(visit),
        n: visit.n,
        date: visit.date,
        // a 0 km (delivery) reading is never shown as "0 km": the odometer column says "new vehicle" instead
        km: visit.km > 0 ? withUnit(fmt(visit.km), 'km') : '',
        // the tag stays English for its style; only what is shown is translated
        tags: (visit.tags || []).map(tag => ({ label: tagLabel(tag, t), style: tagStyle(tag) })),
        summary: summaryOf(visit, t),
        partsLabel: t('journey.parts', { count: parts.length }),
        flag: !!visit.flag,
        hasDetail,
        isOpen,
        dealer: visit.dealer || '',
        complaint: visit.complaint
            ? {
                  text: visit.complaint.text,
                  original: visit.complaint.original || '',
                  assessment: visit.complaint.assessment || '',
              }
            : null,
        ops: ops.map(o => ({
            code: o.code || '',
            showCode: showCodes,
            text: o.text,
            original: o.original || '',
            maint: !!o.maint,
            paint: !!o.paint,
        })),
        parts: parts.map(p => ({ no: p.no || '', text: p.text || '', qty: p.qty || '' })),
        refs,
    };
};

export const buildVisits = (timeline, open, t) =>
    ((timeline && timeline.visits) || []).map((v, i) => buildVisit(v, i, open, t));

const isKeyRead = visit => (visit.raw.tags || []).includes('Key read') && !visit.hasDetail && !visit.raw.flag;

// Consecutive key reads with no work and no drop between them collapse into one row (never when filtered)
const groupKeyReads = (visits, filtered) =>
    visits.reduce((units, visit) => {
        const last = units[units.length - 1];
        const previous = last && last.members[last.members.length - 1];
        const noDrop = previous && (previous.raw.km == null || visit.raw.km == null || previous.raw.km >= visit.raw.km);
        if (!filtered && last && isKeyRead(visit) && isKeyRead(previous) && noDrop) last.members.push(visit);
        else units.push({ members: [visit] });
        return units;
    }, []);

const nodeKind = (raw, older) => {
    const tags = raw.tags || [];
    if (raw.flag) return 'k-flag';
    if (tags.some(tag => tag === 'Maintenance' || tag === 'Service' || tag === 'Inspection')) return 'k-maint';
    if (tags.includes('Key read')) return 'k-key';
    if (!older && raw.km != null && raw.km <= 100) return 'k-reg';
    return 'k-other';
};

const gapKind = (big, drop) => {
    if (big) return 'big';
    return drop ? 'drop' : '';
};

// what the odometer column says under the reading: a delivery reading or a key read
const kmSideLabel = (km, kind, t) => {
    if (km === 0) return t('journey.newVehicle');
    return kind === 'k-key' ? t('journey.keyRead') : '';
};

// The gap below a visit: grey up to 12 months, pink over 12 months, amber when the next older reading is higher
const buildGap = (oldest, older, filtered, t) => {
    if (!older) return null;

    const months = oldest.y * 12 + oldest.m - (older.y * 12 + older.m);
    const kmDiff = oldest.raw.km != null && older.raw.km != null ? oldest.raw.km - older.raw.km : null;
    const drop = kmDiff != null && kmDiff < 0;
    const big = !filtered && !drop && months >= 12;

    let label;
    if (drop) label = t('journey.dropGap', { km: fmt(-kmDiff) });
    else if (big)
        label = t('journey.bigGap', { count: months }) + (kmDiff != null ? ` · +${withUnit(fmt(kmDiff), 'km')}` : '');
    else
        label =
            (kmDiff != null ? `+${withUnit(fmt(kmDiff), 'km')} · ` : '') +
            (months <= 0 ? t('journey.sameMonth') : t('journey.months', { count: months }));

    return { label, kind: gapKind(big, drop) };
};

const buildKeyReadRow = (unit, open, t) => {
    const newest = unit.members[0];
    const oldest = unit.members[unit.members.length - 1];
    const key = `kg${newest.key}`;
    const isOpen = !!open[key];

    return {
        ...newest,
        key,
        date: oldest.date === newest.date ? newest.date : `${oldest.date} – ${newest.date}`,
        n: null,
        tags: [{ label: t('journey.keyReads', { count: unit.members.length }), style: 'neutral' }],
        summary: t('journey.keyReadSummary', { from: fmt(oldest.raw.km), to: fmt(newest.raw.km) }),
        flag: false,
        dealer: '',
        hasDetail: true,
        isOpen,
        complaint: null,
        parts: [],
        refs: [],
        ops: unit.members.map(m => ({
            text: `${m.date} · ${m.km}${m.n != null ? ` · ${t('journey.visitNo', { n: m.n })}` : ''}`,
        })),
        group: unit.members.length,
    };
};

// The journey: visits newest first, filtered by car area and year, with key-read runs, gaps and year headers
export const buildJourney = (visits, { open, zoneVisits, year, allVisits }, t) => {
    const filtered = !!zoneVisits || year != null;
    const shown = visits.filter(v => (!zoneVisits || zoneVisits.has(v.raw.n)) && (year == null || v.y === year));
    const units = groupKeyReads(shown, filtered);

    const items = units.map((unit, i) => {
        const newest = unit.members[0];
        const oldest = unit.members[unit.members.length - 1];
        const older = units[i + 1] ? units[i + 1].members[0] : null;
        const newer = units[i - 1] ? units[i - 1].members[0] : null;
        const index = visits.indexOf(newest);
        const kind = nodeKind(newest.raw, older);
        const row = unit.members.length > 1 ? buildKeyReadRow(unit, open, t) : newest;

        return {
            ...row,
            yearHead: !newer || newer.y !== newest.y,
            kmSide: kmSideLabel(newest.raw.km, kind, t),
            hasKm: newest.raw.km > 0,
            node: kind,
            hidden: !filtered && !allVisits && visits.length > VISIT_LIMIT && index >= VISIT_LIMIT,
            gap: buildGap(oldest, older, filtered, t),
        };
    });

    return {
        items,
        filtered,
        shownCount: shown.length,
        total: visits.length,
        empty: filtered && shown.length === 0,
        hasMore: !filtered && visits.length > VISIT_LIMIT,
    };
};
